// CRM Pools — rehouses the classic MailboxPools screen into the Deliverability shell.
// Master-detail: pool rail (name + member count) + pool detail (members list, create/
// add/remove/delete). A pool is a named set of mailboxes; membership grants a client
// workspace access to a mailbox.
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';

const ST = { active: 'crmpl-dot-green', error: 'crmpl-dot-rose', paused: 'crmpl-dot-amber' };
function StatusDot({ status }) {
  return <span className={'crmpl-dot ' + (ST[status] || 'crmpl-dot-slate')} />;
}

function fmtDate(v) {
  if (!v) return 'never';
  const d = new Date(v);
  if (isNaN(d)) return 'never';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
function fmtDateTime(v) {
  if (!v) return 'never';
  const d = new Date(v);
  if (isNaN(d)) return 'never';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ', ' +
         d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export default function CrmPools({ clientId }) {
  const { getToken } = useAuth();
  const [pools, setPools] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [allMailboxes, setAllMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [addingMember, setAddingMember] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true); setError(null);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [pRes, mRes] = await Promise.all([
        fetch(`${API_BASE}/clients/${clientId}/pools`, { headers }),
        fetch(`${API_BASE}/admin/mailboxes`, { headers }),
      ]);
      if (!pRes.ok) throw new Error(`Pools failed (${pRes.status})`);
      const poolsData = await pRes.json();
      setPools(poolsData);
      if (mRes.ok) {
        const mb = await mRes.json();
        setAllMailboxes(Array.isArray(mb) ? mb : mb.mailboxes || []);
      }
      setSelectedId(prev => prev ?? (poolsData[0]?.id || null));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [clientId, getToken]);

  useEffect(() => { load(); }, [load]);

  const selected = pools.find(p => p.id === selectedId) || null;

  if (loading) return <div className="crmpl-loading">Loading pools…</div>;
  if (error) return <div className="crmpl-error">Error: {error}</div>;

  return (
    <div className="crmpl-root">
      <div className="crmpl-rail">
        <div className="crmpl-rail-head">
          <span className="crmpl-rail-title">Pools ({pools.length})</span>
          <button className="crmpl-btn crmpl-btn--primary crmpl-btn--sm" onClick={() => setCreating(true)}>+ New</button>
        </div>
        <div className="crmpl-rail-list">
          {pools.length === 0 ? (
            <div className="crmpl-rail-empty">No pools yet.</div>
          ) : pools.map(p => (
            <button
              key={p.id}
              className={'crmpl-rail-item' + (p.id === selectedId ? ' crmpl-rail-item--on' : '')}
              onClick={() => setSelectedId(p.id)}
            >
              <div className="crmpl-rail-name">{p.name}</div>
              <div className="crmpl-rail-sub">{p.members?.length || 0} mailbox{(p.members?.length || 0) !== 1 ? 'es' : ''}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="crmpl-pane">
        {selected ? (
          <PoolDetail
            pool={selected}
            clientId={clientId}
            onChange={load}
            onDeleted={() => { setSelectedId(null); load(); }}
            onAddMember={() => setAddingMember(true)}
          />
        ) : (
          <div className="crmpl-pane-empty">
            {pools.length === 0 ? 'Create your first pool to group mailboxes for rotated sending.' : 'Select a pool to view its members.'}
          </div>
        )}
      </div>

      {creating && (
        <CreatePoolModal
          clientId={clientId}
          allMailboxes={allMailboxes}
          onClose={() => setCreating(false)}
          onCreated={(newId) => { setCreating(false); setSelectedId(newId); load(); }}
        />
      )}
      {addingMember && selected && (
        <AddMemberModal
          poolId={selected.id}
          allMailboxes={allMailboxes}
          existingIds={new Set(selected.members?.map(m => m.mailbox_id) || [])}
          onClose={() => setAddingMember(false)}
          onAdded={() => { setAddingMember(false); load(); }}
        />
      )}
    </div>
  );
}

function PoolDetail({ pool, clientId, onChange, onDeleted, onAddMember }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(pool.name);
  const [description, setDescription] = useState(pool.description || '');

  useEffect(() => {
    setName(pool.name);
    setDescription(pool.description || '');
    setEditing(false);
  }, [pool.id, pool.name, pool.description]);

  const save = async () => {
    if (!name.trim()) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/pools/${pool.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), description: description.trim() || null }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      setEditing(false);
      toast.success('Saved');
      onChange();
    } catch (e) { toast.error(e.message); }
  };

  const removeMember = async (mailboxId, label) => {
    if (!window.confirm(`Remove ${label} from this pool?`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/pools/${pool.id}/members/${mailboxId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Remove failed (${res.status})`);
      onChange();
    } catch (e) { toast.error(e.message); }
  };

  const deletePool = async () => {
    if (!window.confirm(`Delete pool "${pool.name}"? Mailboxes themselves are kept; only the grouping is removed. Campaigns using only this pool will need a new pool or mailboxes assigned.`)) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/pools/${pool.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      toast.success('Pool deleted');
      onDeleted();
    } catch (e) { toast.error(e.message); }
  };

  const members = pool.members || [];
  const activeCount = members.filter(m => m.status === 'active').length;
  const capacity = members.reduce((s, m) => s + (m.daily_cap || 0), 0);
  const sentToday = members.reduce((s, m) => s + (m.sent_today || 0), 0);

  return (
    <div className="crmpl-detail">
      <div className="crmpl-hero">
        <div className="crmpl-hero-main">
          <div className="crmpl-hero-top">
            {editing ? (
              <input className="crmpl-title-input" value={name} onChange={e => setName(e.target.value)} autoFocus />
            ) : (
              <h2 className="crmpl-h2">{pool.name}</h2>
            )}
          </div>
          <div className="crmpl-hero-meta">
            <span className="crmpl-hero-stat"><strong>{members.length}</strong> mailbox{members.length !== 1 ? 'es' : ''}</span>
            <span className="crmpl-hero-dot">·</span>
            <span className="crmpl-hero-stat"><strong>{activeCount}</strong> active</span>
            <span className="crmpl-hero-dot">·</span>
            <span className="crmpl-hero-stat"><strong>{sentToday}</strong>/{capacity} sent today</span>
            <span className="crmpl-hero-dot">·</span>
            <span className="crmpl-hero-stat crmpl-muted">Created {fmtDate(pool.created_at)}</span>
          </div>
          {editing ? (
            <div className="crmpl-hero-edit">
              <div className="crmpl-edit-field">
                <label className="crmpl-lbl">Description</label>
                <input className="crmpl-input crmpl-full" value={description} onChange={e => setDescription(e.target.value)} placeholder="What is this pool for?" />
              </div>
            </div>
          ) : (
            <div className="crmpl-hero-desc">
              {pool.description ? <span className="crmpl-hero-descval">{pool.description}</span> : <span className="crmpl-muted">No description</span>}
            </div>
          )}
        </div>
        <div className="crmpl-hero-actions">
          {editing ? (
            <>
              <button className="crmpl-btn crmpl-btn--primary crmpl-btn--sm" onClick={save}>Save</button>
              <button className="crmpl-btn crmpl-btn--sm" onClick={() => setEditing(false)}>Cancel</button>
            </>
          ) : (
            <button className="crmpl-btn crmpl-btn--sm" onClick={() => setEditing(true)}>Edit</button>
          )}
        </div>
      </div>

      <div className="crmpl-members-head">
        <span className="crmpl-members-title">Mailboxes ({members.length})</span>
        <button className="crmpl-btn crmpl-btn--primary crmpl-btn--sm" onClick={onAddMember}>+ Add mailboxes</button>
      </div>

      {members.length === 0 ? (
        <div className="crmpl-members-empty">No mailboxes in this pool yet. Add mailboxes to start sending from it.</div>
      ) : (
        <div className="crmpl-rows">
          {members.map(m => {
            const cap = m.daily_cap || 0;
            const sent = m.sent_today || 0;
            const pct = cap > 0 ? Math.min(100, (sent / cap) * 100) : 0;
            return (
              <div className="crmpl-row" key={m.mailbox_id}>
                <span className={'crmpl-row-dot ' + (ST[m.status] || 'crmpl-dot-slate')} />
                <div className="crmpl-row-id">
                  <button className="crmpl-row-email" onClick={() => navigate(`/clients/${clientId}/crm/deliverability/mailboxes/${m.mailbox_id}`)}>
                    {m.email_address}
                  </button>
                  <div className="crmpl-row-sub">
                    <span className="crmpl-row-prov">{m.provider}</span>
                    <span className="crmpl-row-sep">·</span>
                    <span className="crmpl-row-status">{m.status || 'unknown'}</span>
                    <span className="crmpl-row-sep">·</span>
                    <span>last used {fmtDateTime(m.last_used_at)}</span>
                  </div>
                </div>
                <div className="crmpl-row-pace">
                  <div className="crmpl-row-pacebar"><div className="crmpl-row-pacefill" style={{ width: pct + '%' }} /></div>
                  <div className="crmpl-row-pacelbl">{sent}<span className="crmpl-row-ceil"> / {cap || '—'}</span> today</div>
                </div>
                <div className="crmpl-row-actions">
                  <button className="crmpl-remove" onClick={() => removeMember(m.mailbox_id, m.email_address)}>Remove</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="crmpl-danger">
        <span className="crmpl-danger-text">Delete this pool. The mailboxes themselves are kept and can be re-grouped.</span>
        <button className="crmpl-btn crmpl-btn--danger crmpl-btn--sm" onClick={deletePool}>Delete pool</button>
      </div>
    </div>
  );
}

function MailboxPicker({ mailboxes, selected, onToggle, query, setQuery }) {
  const filtered = mailboxes.filter(m => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (m.email_address || '').toLowerCase().includes(q) || (m.display_name || '').toLowerCase().includes(q);
  });
  return (
    <>
      <input className="crmpl-input crmpl-full" placeholder="Search mailboxes…" value={query} onChange={e => setQuery(e.target.value)} />
      <div className="crmpl-picker">
        {filtered.length === 0 ? (
          <div className="crmpl-picker-empty">No mailboxes.</div>
        ) : filtered.map(m => {
          const checked = selected.has(m.id);
          return (
            <label key={m.id} className={'crmpl-picker-row' + (checked ? ' crmpl-picker-row--on' : '')}>
              <input type="checkbox" checked={checked} onChange={() => onToggle(m.id)} />
              <div className="crmpl-picker-info">
                <div className="crmpl-picker-email">{m.email_address}</div>
                <div className="crmpl-picker-meta">{m.provider} · {m.status}</div>
              </div>
            </label>
          );
        })}
      </div>
    </>
  );
}

function CreatePoolModal({ clientId, allMailboxes, onClose, onCreated }) {
  const { getToken } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const create = async () => {
    if (!name.trim()) { setError('Name required'); return; }
    setSaving(true); setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/clients/${clientId}/pools`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() || null,
          mailbox_ids: Array.from(selected),
        }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || `Create failed (${res.status})`); }
      const data = await res.json();
      onCreated(data.pool_id);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="crmpl-overlay" onClick={onClose}>
      <div className="crmpl-dialog" onClick={e => e.stopPropagation()}>
        <h3 className="crmpl-dialog-title">New pool</h3>
        <div className="crmpl-field">
          <label className="crmpl-lbl">Name</label>
          <input className="crmpl-input crmpl-full" value={name} onChange={e => setName(e.target.value)} autoFocus placeholder="e.g. UK accountants outbound" />
        </div>
        <div className="crmpl-field">
          <label className="crmpl-lbl">Description (optional)</label>
          <input className="crmpl-input crmpl-full" value={description} onChange={e => setDescription(e.target.value)} />
        </div>
        <div className="crmpl-field">
          <label className="crmpl-lbl">Mailboxes ({selected.size} selected)</label>
          <MailboxPicker mailboxes={allMailboxes} selected={selected} onToggle={toggle} query={query} setQuery={setQuery} />
        </div>
        {error && <div className="crmpl-form-err">{error}</div>}
        <div className="crmpl-dialog-actions">
          <button className="crmpl-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="crmpl-btn crmpl-btn--primary" onClick={create} disabled={saving || !name.trim()}>{saving ? 'Creating…' : 'Create pool'}</button>
        </div>
      </div>
    </div>
  );
}

function AddMemberModal({ poolId, allMailboxes, existingIds, onClose, onAdded }) {
  const { getToken } = useAuth();
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const available = allMailboxes.filter(m => !existingIds.has(m.id));
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const add = async () => {
    if (selected.size === 0) return;
    setSaving(true); setError(null);
    try {
      const token = await getToken();
      for (const id of selected) {
        const res = await fetch(`${API_BASE}/pools/${poolId}/members`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ mailbox_id: id }),
        });
        if (!res.ok) throw new Error(`Add ${id} failed (${res.status})`);
      }
      onAdded();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="crmpl-overlay" onClick={onClose}>
      <div className="crmpl-dialog" onClick={e => e.stopPropagation()}>
        <h3 className="crmpl-dialog-title">Add mailboxes to pool</h3>
        {available.length === 0 ? (
          <div className="crmpl-picker-empty">All mailboxes are already in this pool.</div>
        ) : (
          <MailboxPicker mailboxes={available} selected={selected} onToggle={toggle} query={query} setQuery={setQuery} />
        )}
        {error && <div className="crmpl-form-err">{error}</div>}
        <div className="crmpl-dialog-actions">
          <div className="crmpl-dialog-count">{selected.size} selected</div>
          <div className="crmpl-dialog-btns">
            <button className="crmpl-btn" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="crmpl-btn crmpl-btn--primary" onClick={add} disabled={saving || selected.size === 0}>{saving ? 'Adding…' : `Add ${selected.size || ''}`}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
